import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Plus, Trash2, Eye, X } from 'lucide-react'
import { getDeliveryChallan, createDeliveryChallan, updateDeliveryChallan, getCustomers, getProducts, getCompanySettings } from '../services/api'
import { calculateLineItemTax, calculateInvoiceTotals } from '../lib/gst'
import { formatCurrency, GST_STATES, toInputDate } from '../lib/formatters'
import SearchableSelect from '../components/ui/SearchableSelect'

const EMPTY_LINE = { productId: '', description: '', detailedDescription: '', hsnSac: '', quantity: 1, rate: 0, gstRate: 18, activityDate: '' }

const TRANSPORT_REASONS = ['Supply', 'Approval / Trial', 'Job Work', 'Exhibition', 'Other']

export default function DeliveryChallanForm() {
  const { id } = useParams()
  const navigate = useNavigate()
  const isEdit = !!id

  const [form, setForm] = useState({
    customerId: '', dcDate: toInputDate(new Date()),
    placeOfSupply: '', currency: 'INR',
    billToName: '', billToAddress: '', shipToName: '', shipToAddress: '',
    vehicleNumber: '', transporterName: '', reasonForTransport: '',
    customerNote: '', notes: '',
  })
  const [lineItems, setLineItems] = useState([{ ...EMPTY_LINE }])
  const [customers, setCustomers] = useState([])
  const [products, setProducts] = useState([])
  const [company, setCompany] = useState(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [showPreview, setShowPreview] = useState(false)

  useEffect(() => {
    Promise.all([
      getCustomers({ take: 2000 }),
      getProducts({ take: 500 }),
      isEdit ? getDeliveryChallan(id) : null,
      getCompanySettings().catch(() => null),
    ]).then(([custRes, prodRes, dcRes, compRes]) => {
      if (compRes?.data) setCompany(compRes.data)
      const custData = custRes.data
      setCustomers(Array.isArray(custData) ? custData : custData.customers || [])
      setProducts(Array.isArray(prodRes.data) ? prodRes.data : prodRes.data.products || [])
      if (dcRes) {
        const dc = dcRes.data
        setForm({
          customerId: dc.customerId, dcDate: toInputDate(dc.date),
          placeOfSupply: dc.placeOfSupply?.toString() || '',
          currency: dc.currency,
          billToName: dc.billToName || '', billToAddress: dc.billToAddress || '',
          shipToName: dc.shipToName || '', shipToAddress: dc.shipToAddress || '',
          vehicleNumber: dc.vehicleNumber || '', transporterName: dc.transporterName || '',
          reasonForTransport: dc.reasonForTransport || '',
          customerNote: dc.customerNote || '', notes: dc.notes || '',
        })
        setLineItems(dc.lineItems.map((li) => ({
          productId: li.productServiceId || '', description: li.description, detailedDescription: li.detailedDescription || '',
          hsnSac: li.hsnSacCode || '', quantity: li.qty, rate: Number(li.rate), gstRate: Number(li.gstRate),
          activityDate: li.activityDate ? toInputDate(li.activityDate) : '',
        })))
      }
    }).catch(() => {})
  }, [id, isEdit])

  const handleCustomerChange = (customerId) => {
    setForm((f) => ({ ...f, customerId }))
    const cust = customers.find((c) => c.id === customerId)
    if (cust?.placeOfSupply || cust?.billingStateCode) {
      setForm((f) => ({ ...f, customerId, placeOfSupply: (cust.placeOfSupply || cust.billingStateCode).toString() }))
    }
  }

  const handleProductSelect = (index, productId) => {
    const prod = products.find((p) => p.id === productId)
    if (prod) {
      updateLine(index, {
        productId, description: prod.name, hsnSac: prod.hsnSacCode || '',
        rate: Number(prod.defaultRate), gstRate: Number(prod.gstRate),
      })
    } else {
      updateLine(index, { productId })
    }
  }

  const updateLine = (index, changes) => {
    setLineItems((prev) => prev.map((li, i) => (i === index ? { ...li, ...changes } : li)))
  }

  const addLine = () => setLineItems((prev) => [...prev, { ...EMPTY_LINE }])
  const removeLine = (index) => setLineItems((prev) => prev.filter((_, i) => i !== index))

  const computedLines = lineItems.map((li) => {
    const inclusiveAmount = li.quantity * li.rate
    const tax = calculateLineItemTax(li.gstRate, inclusiveAmount, form.placeOfSupply)
    return { ...li, ...tax, total: inclusiveAmount }
  })
  const totals = calculateInvoiceTotals(computedLines)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setSaving(true)
    try {
      const payload = {
        customerId: form.customerId,
        date: form.dcDate,
        placeOfSupply: form.placeOfSupply ? Number(form.placeOfSupply) : null,
        currency: form.currency,
        billToName: form.billToName || null,
        billToAddress: form.billToAddress || null,
        shipToName: form.shipToName || null,
        shipToAddress: form.shipToAddress || null,
        vehicleNumber: form.vehicleNumber || null,
        transporterName: form.transporterName || null,
        reasonForTransport: form.reasonForTransport || null,
        customerNote: form.customerNote || null,
        notes: form.notes,
        lineItems: computedLines.map((li) => ({
          productServiceId: li.productId || null, description: li.description, detailedDescription: li.detailedDescription || null,
          hsnSacCode: li.hsnSac, activityDate: li.activityDate || null, qty: li.quantity, rate: li.rate, gstRate: li.gstRate,
        })),
      }
      if (isEdit) {
        await updateDeliveryChallan(id, payload)
        navigate(`/delivery-challans/${id}`)
      } else {
        const res = await createDeliveryChallan(payload)
        navigate(`/delivery-challans/${res.data.id}`)
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save delivery challan')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <h1 className="text-2xl font-semibold text-gray-900">
        {isEdit ? 'Edit Delivery Challan' : 'New Delivery Challan'}
      </h1>

      <form onSubmit={handleSubmit} className="space-y-6">
        {error && <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>}

        {/* Header fields */}
        <div className="card grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="label">Customer</label>
            <SearchableSelect
              options={customers.map((c) => ({ value: c.id, label: c.name }))}
              value={form.customerId}
              onChange={handleCustomerChange}
              placeholder="Select customer"
              required
            />
          </div>
          <div>
            <label className="label">Date</label>
            <input type="date" className="input" value={form.dcDate} onChange={(e) => setForm({ ...form, dcDate: e.target.value })} onKeyDown={(e) => e.preventDefault()} required />
          </div>
          <div>
            <label className="label">Place of Supply</label>
            <SearchableSelect
              options={GST_STATES.map((s) => ({ value: String(s.code), label: `${s.code} - ${s.name}` }))}
              value={form.placeOfSupply}
              onChange={(val) => setForm({ ...form, placeOfSupply: val })}
              placeholder="Select state"
            />
          </div>
          <div>
            <label className="label">Currency</label>
            <select className="select" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })}>
              <option value="INR">INR</option>
              <option value="USD">USD</option>
            </select>
          </div>
        </div>

        {/* Transport details (optional) */}
        <div className="card">
          <h2 className="text-sm font-medium text-gray-700 mb-3">Transport Details <span className="text-gray-400 font-normal">(optional)</span></h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="label">Vehicle Number</label>
              <input className="input font-mono" value={form.vehicleNumber} onChange={(e) => setForm({ ...form, vehicleNumber: e.target.value.toUpperCase() })} placeholder="KA01AB1234" />
            </div>
            <div>
              <label className="label">Transporter Name</label>
              <input className="input" value={form.transporterName} onChange={(e) => setForm({ ...form, transporterName: e.target.value })} placeholder="Transporter / courier" />
            </div>
            <div>
              <label className="label">Reason for Transport</label>
              <select className="select" value={form.reasonForTransport} onChange={(e) => setForm({ ...form, reasonForTransport: e.target.value })}>
                <option value="">Select reason</option>
                {TRANSPORT_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* Bill To */}
        <div className="card">
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm font-medium text-gray-700">Bill To <span className="text-gray-400 font-normal">(override customer default)</span></label>
            <div className="flex gap-3">
              {!form.billToName && !form.billToAddress && form.customerId && (
                <button type="button" onClick={() => {
                  const cust = customers.find((c) => c.id === form.customerId)
                  if (cust) setForm((f) => ({ ...f, billToName: cust.name, billToAddress: [cust.billingAddress, [cust.billingCity, cust.billingState, cust.billingPincode].filter(Boolean).join(', ')].filter(Boolean).join('\n') }))
                }} className="text-xs text-brand-600 hover:text-brand-700">Copy from customer</button>
              )}
              {(form.billToName || form.billToAddress) && (
                <button type="button" onClick={() => setForm((f) => ({ ...f, billToName: '', billToAddress: '' }))} className="text-xs text-gray-400 hover:text-gray-600">Clear</button>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">Name</label>
              <input className="input" value={form.billToName} onChange={(e) => setForm({ ...form, billToName: e.target.value })} placeholder="Leave blank to use customer's name" />
            </div>
            <div>
              <label className="label">Billing Address</label>
              <textarea className="input" rows={2} value={form.billToAddress} onChange={(e) => setForm({ ...form, billToAddress: e.target.value })} placeholder="Leave blank to use customer's default address" />
            </div>
          </div>
        </div>

        {/* Ship To */}
        <div className="card">
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm font-medium text-gray-700">Ship To <span className="text-gray-400 font-normal">(delivery address)</span></label>
            {!form.shipToName && !form.shipToAddress && (
              <button type="button" onClick={() => {
                const cust = customers.find((c) => c.id === form.customerId)
                if (cust) setForm((f) => ({ ...f, shipToName: cust.name, shipToAddress: [cust.shippingAddress || cust.billingAddress, [cust.shippingCity || cust.billingCity, cust.shippingState || cust.billingState, cust.shippingPincode || cust.billingPincode].filter(Boolean).join(', ')].filter(Boolean).join('\n') }))
              }} className="text-xs text-brand-600 hover:text-brand-700">Copy from customer</button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">Recipient Name</label>
              <input className="input" value={form.shipToName} onChange={(e) => setForm({ ...form, shipToName: e.target.value })} placeholder="Name / Hospital / Clinic" />
            </div>
            <div>
              <label className="label">Shipping Address</label>
              <textarea className="input" rows={2} value={form.shipToAddress} onChange={(e) => setForm({ ...form, shipToAddress: e.target.value })} placeholder="Full address including city, state, pincode" />
            </div>
          </div>
        </div>

        {/* Line items */}
        <div className="card !p-0 overflow-hidden">
          <div className="px-6 pt-5 pb-3 border-b border-gray-100">
            <h2 className="text-lg font-medium text-gray-900">Line Items</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50/80 text-left text-gray-500 text-xs uppercase tracking-wider">
                  <th className="pl-6 pr-2 py-3 font-medium w-[200px]">Product / Service</th>
                  <th className="px-2 py-3 font-medium">Description</th>
                  <th className="px-2 py-3 font-medium w-[100px]">HSN/SAC</th>
                  <th className="px-2 py-3 font-medium w-[80px] text-center">Qty</th>
                  <th className="px-2 py-3 font-medium w-[120px] text-right">Rate (incl.)</th>
                  <th className="px-2 py-3 font-medium w-[90px]">GST %</th>
                  <th className="px-2 py-3 font-medium w-[110px] text-right">Amount</th>
                  <th className="px-2 pr-6 py-3 w-[40px]"></th>
                </tr>
              </thead>
              <tbody>
                {lineItems.map((li, i) => (
                  <React.Fragment key={i}>
                    <tr className="border-b border-gray-50 hover:bg-gray-50/30 transition-colors">
                      <td className="pl-6 pr-2 py-2.5">
                        <select className="select text-sm" value={li.productId} onChange={(e) => handleProductSelect(i, e.target.value)}>
                          <option value="">Select...</option>
                          {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                      </td>
                      <td className="px-2 py-2.5">
                        <input className="input text-sm" value={li.description} onChange={(e) => updateLine(i, { description: e.target.value })} placeholder="Item name" required />
                      </td>
                      <td className="px-2 py-2.5">
                        <input className="input text-sm font-mono" value={li.hsnSac} onChange={(e) => updateLine(i, { hsnSac: e.target.value })} placeholder="Code" />
                      </td>
                      <td className="px-2 py-2.5">
                        <input type="number" min="1" className="input text-sm text-center" value={li.quantity} onChange={(e) => updateLine(i, { quantity: Number(e.target.value) })} />
                      </td>
                      <td className="px-2 py-2.5">
                        <input type="number" min="0" step="0.01" className="input text-sm text-right" value={li.rate} onChange={(e) => updateLine(i, { rate: Number(e.target.value) })} placeholder="0.00" />
                      </td>
                      <td className="px-2 py-2.5">
                        <select className="select text-sm" value={li.gstRate} onChange={(e) => updateLine(i, { gstRate: Number(e.target.value) })}>
                          <option value={0}>0%</option>
                          <option value={5}>5%</option>
                          <option value={12}>12%</option>
                          <option value={18}>18%</option>
                          <option value={28}>28%</option>
                        </select>
                      </td>
                      <td className="px-2 py-2.5 text-right font-semibold text-gray-900 whitespace-nowrap">
                        {formatCurrency(computedLines[i]?.total || 0, form.currency)}
                      </td>
                      <td className="px-2 pr-6 py-2.5 text-center">
                        {lineItems.length > 1 && (
                          <button type="button" onClick={() => removeLine(i)} className="p-1 text-gray-300 hover:text-red-500 transition-colors rounded" title="Remove line">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="pl-6 pr-2 pb-3" />
                      <td colSpan={6} className="px-2 pb-3">
                        <textarea
                          className="input text-xs resize-none"
                          rows={li.detailedDescription ? 2 : 1}
                          value={li.detailedDescription}
                          onChange={(e) => updateLine(i, { detailedDescription: e.target.value })}
                          placeholder="Detailed description / specs..."
                          onFocus={(e) => { if (e.target.rows < 2) e.target.rows = 2 }}
                          onBlur={(e) => { if (!e.target.value) e.target.rows = 1 }}
                        />
                      </td>
                      <td />
                    </tr>
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-6 py-3 border-t border-gray-100">
            <button type="button" onClick={addLine} className="btn-ghost flex items-center gap-1.5 text-sm text-brand-600 hover:text-brand-700">
              <Plus className="h-4 w-4" /> Add Line Item
            </button>
          </div>
        </div>

        {/* Totals */}
        <div className="flex justify-end">
          <div className="card w-80">
            <div className="space-y-2.5 text-sm">
              <div className="flex justify-between text-gray-500"><span>Subtotal</span><span className="text-gray-700">{formatCurrency(totals.subtotal, form.currency)}</span></div>
              {totals.cgst > 0 && <div className="flex justify-between text-gray-500"><span>CGST</span><span className="text-gray-700">{formatCurrency(totals.cgst, form.currency)}</span></div>}
              {totals.sgst > 0 && <div className="flex justify-between text-gray-500"><span>SGST</span><span className="text-gray-700">{formatCurrency(totals.sgst, form.currency)}</span></div>}
              {totals.igst > 0 && <div className="flex justify-between text-gray-500"><span>IGST</span><span className="text-gray-700">{formatCurrency(totals.igst, form.currency)}</span></div>}
              <div className="flex justify-between font-semibold text-lg border-t border-gray-200 pt-3 mt-1">
                <span className="text-gray-900">Total</span><span className="text-brand-700">{formatCurrency(totals.total, form.currency)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Customer Note */}
        <div className="card">
          <label className="label">Note to Customer <span className="text-gray-400 font-normal">(shown on delivery challan)</span></label>
          <textarea className="input" rows={2} value={form.customerNote} onChange={(e) => setForm({ ...form, customerNote: e.target.value })} placeholder="Special handling instructions, etc." />
        </div>

        {/* Internal Notes */}
        <div className="card">
          <label className="label">Internal Notes <span className="text-gray-400 font-normal">(not shown on DC)</span></label>
          <textarea className="input" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Private notes for your team..." />
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3">
          <button type="button" onClick={() => navigate(-1)} className="btn-secondary">Cancel</button>
          <button type="submit" disabled={saving} className="btn-primary">
            {saving ? 'Saving...' : isEdit ? 'Update Draft' : 'Create Draft'}
          </button>
        </div>
      </form>
    </div>
  )
}
